import { AuthApiService } from "./authApi";
import { BulkEmailApiService } from "./bulkEmailApi";
import { UsersApiService } from "./usersApi";

// Create instances of all API services
const authApi = new AuthApiService();
const bulkEmailApi = new BulkEmailApiService();
const usersApi = new UsersApiService();

// Main API service that combines all services
class ApiService {
  private authToken: string | null = null;

  setAuthToken(token: string) {
    this.authToken = token;
    authApi.setAuthToken(token);
    bulkEmailApi.setAuthToken(token);
    usersApi.setAuthToken(token);
  }

  clearAuthToken() {
    this.authToken = null;
    authApi.clearAuthToken();
    bulkEmailApi.clearAuthToken();
    usersApi.clearAuthToken();
  }

  async get<T>(endpoint: string): Promise<T> {
    const { config } = await import("../config/environment");
    const url = `${config.apiUrl}${endpoint}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: this.authToken
          ? `Bearer ${this.authToken}`
          : `Bearer ${config.adminSecret}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const { config } = await import("../config/environment");
    const url = `${config.apiUrl}${endpoint}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: this.authToken
          ? `Bearer ${this.authToken}`
          : `Bearer ${config.adminSecret}`,
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const { config } = await import("../config/environment");
    const url = `${config.apiUrl}${endpoint}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: this.authToken
          ? `Bearer ${this.authToken}`
          : `Bearer ${config.adminSecret}`,
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  }

  async delete<T>(endpoint: string): Promise<T> {
    const { config } = await import("../config/environment");
    const url = `${config.apiUrl}${endpoint}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: this.authToken
          ? `Bearer ${this.authToken}`
          : `Bearer ${config.adminSecret}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    return await response.json();
  }

  async getUserTrackingStats(days: number = 30): Promise<{
    data: import("../types").UserTrackingStats;
  }> {
    return this.get(`/user-tracking/stats?days=${days}`);
  }
  async getUserTrackingAnalytics(
    days: number
  ): Promise<{ data: import("../types").UserTrackingAnalytics }> {
    return this.get(`/user-tracking/analytics?days=${days}`);
  }
  async getAllUserTracking(
    page: number,
    limit: number,
    sortBy: string,
    sortOrder: string
  ): Promise<{
    data: import("../types").UserTrackingData[];
    pagination: any;
  }> {
    return this.get(
      `/user-tracking/all?page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`
    );
  }
  async getUserTrackingSummary(
    userId: string
  ): Promise<{ data: import("../types").UserTrackingData }> {
    return this.get(`/user-tracking/summary/${userId}`);
  }
  async getUsersByVersionFromTracking(
    version: string,
    platform: string | undefined
  ): Promise<{ data: import("../types").UserTrackingData[] }> {
    const platformParam = platform ? `&platform=${platform}` : "";
    return this.get(`/user-tracking/users/${version}?${platformParam}`);
  }
  async updateUserSession(
    userId: string,
    sessionData: {
      sessionStart?: string;
      sessionEnd?: string;
      duration?: number;
    }
  ): Promise<{ data: any }> {
    return this.put(`/user-tracking/session/${userId}`, sessionData);
  }
  async deleteUserTracking(userId: string): Promise<any> {
    return this.delete(`/user-tracking/${userId}`);
  }
  async cleanupUserTrackingRecords(): Promise<{
    data: { deletedCount: number };
  }> {
    return this.post("/user-tracking/cleanup");
  }
  async getDashboardStats(): Promise<any> {
    return this.get("/admin/all-counts");
  }
  getChatById(id: string) {
    return this.get(`/admin/chats/${id}`);
  }
  getChats(page: number, limit: number, search: string, userId?: string) {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (search) queryParams.append("search", search);
    if (userId) queryParams.append("userId", userId);
    return this.get(`/admin/chats?${queryParams.toString()}`);
  }
  getChatHistory(chatId: string, page: number, limit: number) {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    return this.get(
      `/admin/chats/${chatId}/messages?${queryParams.toString()}`
    );
  }
  // Auth methods

  // Auth API
  async login(credentials: { email: string; password: string }) {
    return authApi.login(credentials);
  }

  async adminLogin(credentials: { email: string; password: string }) {
    return authApi.adminLogin(credentials);
  }

  async logout() {
    return authApi.logout();
  }

  async refreshToken() {
    return authApi.refreshToken();
  }

  async getProfile() {
    return authApi.getProfile();
  }

  async updateProfile(data: any) {
    return authApi.updateProfile(data);
  }

  async changePassword(data: { currentPassword: string; newPassword: string }) {
    return authApi.changePassword(data);
  }

  // Bulk Email API
  async getRecipients(params?: any) {
    return bulkEmailApi.getRecipients(params);
  }

  async addRecipient(recipient: any) {
    return bulkEmailApi.addRecipient(recipient);
  }

  async updateRecipient(id: string, recipient: any) {
    return bulkEmailApi.updateRecipient(id, recipient);
  }

  async deleteRecipient(id: string) {
    return bulkEmailApi.deleteRecipient(id);
  }

  async uploadRecipientsCsv(file: File) {
    return bulkEmailApi.uploadRecipientsCsv(file);
  }

  async getTemplates() {
    const response = await bulkEmailApi.getTemplates();
    // The bulkEmailApi returns { templates: [], total: 0, ... }
    // But we need to return it as { data: { templates: [], total: 0, ... } }
    return { data: response };
  }

  async createTemplate(template: any) {
    const response = await bulkEmailApi.createTemplate(template);
    return { data: response };
  }

  async updateTemplate(id: string, template: any) {
    const response = await bulkEmailApi.updateTemplate(id, template);
    return { data: response };
  }

  async deleteTemplate(id: string) {
    return bulkEmailApi.deleteTemplate(id);
  }

  async sendBulkEmail(data: any) {
    return bulkEmailApi.sendBulkEmail(data);
  }

  async getEmailStats(filters?: any) {
    return bulkEmailApi.getEmailStats(filters);
  }

  async getEmailHistory(params?: any) {
    return bulkEmailApi.getEmailHistory(params);
  }

  async getBulkEmailHistory(params?: any) {
    return bulkEmailApi.getEmailHistory(params);
  }

  // Users API
  async getUsers(params?: any) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.platform) queryParams.append("platform", params.platform);

    const endpoint = `/admin/users${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    return this.get(endpoint);
  }

  async getUserById(id: string) {
    return this.get(`/admin/users/${id}`);
  }

  async updateUser(id: string, userData: any) {
    return usersApi.updateUser(id, userData);
  }

  async deleteUser(id: string) {
    return usersApi.deleteUser(id);
  }

  async suspendUser(id: string) {
    return usersApi.suspendUser(id);
  }

  async unsuspendUser(id: string) {
    return usersApi.unsuspendUser(id);
  }

  async adminUpdateUser(
    id: string,
    data: Partial<{
      firstName: string;
      lastName: string;
      username: string;
      phoneNumber: string;
      bio: string;
      gender: string;
    }>
  ) {
    return usersApi.adminUpdateUser(id, data);
  }

  async updateUserFeatureFlags(
    id: string,
    featureFlags: Record<string, boolean>
  ) {
    return usersApi.updateUserFeatureFlags(id, featureFlags);
  }

  async getUserStats() {
    return usersApi.getUserStats();
  }

  async getUserTracking(id: string) {
    return usersApi.getUserTracking(id);
  }

  // Groups API
  async getGroups() {
    return bulkEmailApi.getGroups();
  }

  async createGroup(groupData: any) {
    return bulkEmailApi.createGroup(groupData);
  }

  async updateGroup(id: string, groupData: any) {
    return bulkEmailApi.updateGroup(id, groupData);
  }

  async deleteGroup(id: string) {
    return bulkEmailApi.deleteGroup(id);
  }

  // Legacy methods for backward compatibility
  async getRecipientsFromBackend(
    page: number = 1,
    limit: number = 50,
    search: string = "",
    status: string = "all"
  ) {
    return this.getRecipients({ page, limit, search, status });
  }

  async getUsersFromBackend(
    page: number = 1,
    limit: number = 50,
    search: string = "",
    status: string = "all"
  ) {
    return this.getUsers({ page, limit, search, status });
  }

  // Additional missing methods
  async getCurrentUser() {
    return this.getProfile();
  }

  async getOnlineUsersCount() {
    return this.get("/admin/online-users-count");
  }

  async getLiveUsers() {
    return this.get("/admin/live-users");
  }

  async getMessagesCount() {
    return this.get("/admin/messages-count");
  }

  async getMessages(
    page: number,
    limit: number,
    search: string,
    chatId?: string
  ) {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (search) queryParams.append("search", search);
    if (chatId) queryParams.append("chatId", chatId);

    const endpoint = `/admin/messages?${queryParams.toString()}`;
    return this.get(endpoint);
  }

  async getMessageById(messageId: string) {
    return this.get(`/admin/messages/${messageId}`);
  }

  async getSupportChats(
    page: number,
    limit: number,
    status: string,
    search?: string
  ) {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page.toString());
    queryParams.append("limit", limit.toString());
    if (status) queryParams.append("status", status);
    if (search) queryParams.append("search", search);

    const endpoint = `/admin/support-chats?${queryParams.toString()}`;
    return this.get(endpoint);
  }

  async getSupportChatById(chatId: string) {
    return this.get(`/admin/support-chats/${chatId}`);
  }

  async sendSupportChatReply(chatId: string, message: string) {
    return this.post(`/admin/support-chats/${chatId}/reply`, { message });
  }

  async sendSupportChatEmailReply(chatId: string, message: string) {
    return this.post(`/admin/support-chats/${chatId}/email-reply`, { message });
  }

  async updateSupportChatStatus(chatId: string, status: string) {
    return this.put(`/admin/support-chats/${chatId}/status`, { status });
  }

  async getSupportChatsStats() {
    return this.get("/admin/support-chats-stats");
  }

  async getAppVersionStats() {
    return usersApi.getAppVersionStats();
  }
}

// Export singleton instance
const apiService = new ApiService();
export default apiService;
